import { trackError } from '@billy/track-error'
import type { AxiosResponse } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import {
  useUpdateMultipleOrganizationSettings,
  useUpdateOrganization,
  useUserOrganization,
} from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../enums/queryKeys'
import { zervantSettingsQueryKey } from '../constants/zervantSettingsQueryKey'
import { getPaymentMethodsPayload } from '../modules/paymentsAndReminders'
import type { CompanyInformationFormUpdate } from '../types/companyInformationFormUpdate'
import type { ZervantSettings } from '../types/zervantSettings'
import { mapCompanyFormToValues } from '../utils/mapCompanyFormToValues'
import { useUpdateInvoicingSettings } from '../writer'

interface UseUpdateCompanyInformationProps {
  onError?: (error: unknown) => void
  onSuccess?: (formData: CompanyInformationFormUpdate) => void
}

export const useUpdateCompanyInformation = (props: UseUpdateCompanyInformationProps = {}) => {
  const { onError, onSuccess } = props
  const queryClient = useQueryClient()
  const { organization } = useUserOrganization()

  const { update: updateInvoicingSettings } = useUpdateInvoicingSettings()
  const { updateAsync: updateOrganizationAsync } = useUpdateOrganization()
  const { updateAsync: updateMultipleOrganizationSettingsAsync } = useUpdateMultipleOrganizationSettings()

  const {
    mutate: update,
    isLoading: isProcessing,
    ...rest
  } = useMutation(
    async (formData: CompanyInformationFormUpdate) => {
      if (!organization) {
        throw new Error('Organization is not loaded')
      }

      const cachedSettingsResponse = queryClient.getQueryData<AxiosResponse<ZervantSettings>>(zervantSettingsQueryKey)

      if (!cachedSettingsResponse?.data) {
        throw new Error('Invoicing settings cache is empty')
      }

      const isSiretPending = !formData.registrationNo && formData.isSiretPendingAssignment

      await Promise.all([
        updateInvoicingSettings({
          values: {
            company: mapCompanyFormToValues(cachedSettingsResponse.data.values.company, formData),
            ...(formData.paymentMethods && {
              paymentMethods: getPaymentMethodsPayload(
                cachedSettingsResponse.data.values.paymentMethods,
                formData.paymentMethods,
              ),
            }),
          },
        }),
        updateMultipleOrganizationSettingsAsync({
          organizationId: organization.id,
          payload: {
            isCompanyInformationAdded: '1',
            siretPendingAssignmentShowTopBar: isSiretPending ? '1' : '0',
          },
        }),
      ])

      await updateOrganizationAsync({
        organizationId: organization.id,
        payload: {
          addressLine2: formData.addressLine2,
          city: formData.companyCity,
          isVatExempted: formData.isVatExempted,
          name: formData.companyName,
          registrationNo: formData.registrationNo,
          street: formData.companyAddress,
          vatNo: formData.vatNo,
          zipcode: formData.companyZipCode,
        },
      })

      await Promise.all([
        queryClient.invalidateQueries(QueryKeys.UserOrganizationSettings),
        queryClient.invalidateQueries([QueryKeys.UserOrganization, organization.id]),
      ])
    },
    {
      onError: (error) => {
        trackError(error)
        onError?.(error)
      },
      onSuccess: (_, formData) => {
        onSuccess?.(formData)
      },
    },
  )

  return { update, isProcessing, ...rest }
}
